import type { LeadMagnetSchema } from '~/lib/schemas/lead-magnet-schema'
import type { LeadMagnetPage } from '~/utils/lead-magnets'
import { LINKS } from '~/utils/links'
import type { CalculatorState } from './types'

const LINK_CLASS = 'text-green underline'
const HEADING_CLASS = 'mb-1 block font-bold text-gray-dark'

/**
 * OnTrack Terms and Disclosures + TCPA consent, Compliance-approved for this page.
 * Verbatim — do not edit wording. It intentionally differs from the shared PhoneDisclosureBody
 * ("may be sent using" vs "may be sent by using").
 */
function OnTrackConsentBody() {
  return (
    <div className="flex flex-col gap-4 text-neutral-darker">
      <p>
        <strong className={HEADING_CLASS}>Terms and Disclosures</strong>I acknowledge and agree to NerdWallet Wealth
        Partner&apos;s{' '}
        <a href={LINKS.TERMS_OF_USE} target="_blank" rel="noopener noreferrer" className={LINK_CLASS}>
          Terms of Use
        </a>{' '}
        and{' '}
        <a href={LINKS.PRIVACY_POLICY} target="_blank" rel="noopener noreferrer" className={LINK_CLASS}>
          Privacy Policy
        </a>
        , and confirm receipt of NerdWallet Wealth Partner&apos;s{' '}
        <a href={LINKS.ADV_2A} target="_blank" rel="noopener noreferrer" className={LINK_CLASS}>
          Form ADV Part 2A
        </a>{' '}
        and{' '}
        <a href={LINKS.FORM_CRS} target="_blank" rel="noopener noreferrer" className={LINK_CLASS}>
          Form CRS
        </a>
        .
      </p>
      <p>
        <strong className={HEADING_CLASS}>Consent to Marketing Calls and Texts</strong>I provide my express written
        consent to receive marketing calls and text messages at the phone number I provided above from NerdWallet Wealth
        Partners, including their agents and service providers. These communications may be sent using automated
        technology, including an autodialer, selection system, or prerecorded or artificial voice. I provide this
        consent even if my number is listed on any company-specific, state, or federal Do-Not-Call list, or if I have
        previously opted out of receiving telemarketing calls or text messages from NerdWallet. I understand that I may
        opt out of receiving calls or text messages at any time.
      </p>
      <p>
        I certify that the phone number I provided is my own and I am permitted to receive calls and texts at that
        number, and I agree to promptly notify NerdWallet Wealth Partners if I stop using the number entered above.
        Message and data rates may apply. Consent is not required as a condition of using these services.
      </p>
    </div>
  )
}

type InvestableAssetsRange = NonNullable<LeadMagnetSchema['investable_assets_range']>

/** Buckets match INVESTABLE_ASSETS_OPTIONS; lower bounds are inclusive. */
export function savingsToAssetsRange(savings: number): InvestableAssetsRange {
  if (savings < 100_000) return 'UNDER_100K'
  if (savings < 250_000) return 'FROM_100_TO_250K'
  if (savings < 500_000) return 'FROM_250_TO_500K'
  if (savings < 1_000_000) return 'FROM_500K_TO_1M'
  return 'OVER_1M'
}

/** Lead fields the calculator already asked for, so the unlock form doesn't ask twice. */
export function calculatorLeadValues(state: CalculatorState) {
  return {
    age: state.age,
    household_income: state.income,
    investable_assets_range: savingsToAssetsRange(state.savings),
    current_retirement: state.savings,
  }
}

/** In-page unlock form — not a LEAD_MAGNET_PAGES gate. Slug keeps continuity with the retired /lp/retirement_calculator gate. */
export const ONTRACK_UNLOCK_MAGNET: LeadMagnetPage = {
  slug: 'retirement_calculator',
  pageTitle: 'OnTrack Retirement Calculator',
  headline: 'Unlock your results',
  body: null,
  ctaText: 'See my results',
  ctaType: 'reveal',
  disclosure:
    'The calculator is provided for informational and educational purposes only. Results are hypothetical estimates based on the information you provide and should not be construed as investment, tax, or legal advice, or as a guarantee of future results. NWWP is an SEC-registered investment adviser. Registration does not imply a certain level of skill or training. See the General Disclosure.',
  formFields: ['first_name', 'last_name', 'email', 'phone'],
  phoneRequired: true,
  phoneDisclosure: <OnTrackConsentBody />,
  submitVariant: 'funnel',
  disclosureClassName: 'text-body-xs',
}
